// dependencies
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { ObjectID } from "bson";
import React, { RefObject, useEffect } from "react";

// icons
import { faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faExclamationTriangle, faSave } from "@performyard/fontawesome/pro-solid-svg-icons";
// py-components
import { FormList, FormSubscribe, PYForm, PageBanner, SubmitButton, toast } from "@performyard/py-components";
// schema
import { SurveyModelJson, SurveyQuestionJson, UpdateSurveyRequestJson } from "@performyard/service-contracts";

// components
import Card from "../../../../../../components/Card";
import Spacer from "../../../../../../components/Spacer";
import EditQuestionBlock from "./components/EditQuestionBlock";
import QuestionBlock from "./components/QuestionBlock";
import TitleInput from "./components/SurveyTitleInput";

import { useSetters as useSurveyDetailsSetters, useSurveyStatus } from "../../store/store";
// store
import {
	useActions,
	useBuilderBanner,
	useEditingQuestion,
	useHasChanges,
	useOrderedQuestions,
	useSetters,
	useSurveyName,
} from "./store/store";

// api
import { useUpdateSurvey } from "../../../../../../api/hooks/defs/surveys";
// hooks
import useUnsavedChanges from "../../../../../../hooks/useUnsavedChanges";
import { useCompareDraftToSaved } from "../../hooks/useCompareDraftToSaved";
import { useSurvey } from "../../hooks/useSurvey";
import { useSurveyAccessTier } from "../../hooks/useSurveyAccessTier";

import { formatErrorMessage } from "../../../../../../utils/TanstackQueryErrorHandling";
import { cleanQuestions } from "../../utils";

// page templates
import BasePageTemplate from "../../../../../../pageTemplates/BasePageTemplate";
import { TformRef, surveyBuilderSchema } from "../../SurveyDetails";

interface SurveyBuilderProps {
	survey: SurveyModelJson;
	formRef: RefObject<TformRef | null>;
}

export function SurveyBuilder({ survey, formRef }: SurveyBuilderProps) {
	const [ref] = useAutoAnimate();
	const [bannerRef] = useAutoAnimate();

	const { setIsUnsafe, setIsSafe } = useUnsavedChanges({ message: "You have unsaved changes" });

	// store
	const orderedQuestions = useOrderedQuestions();
	const surveyName = useSurveyName();
	const editingQuestion = useEditingQuestion();
	const hasChanges = useHasChanges();
	const builderBanner = useBuilderBanner();
	const surveyStatus = useSurveyStatus();
	const actions = useActions();
	const setters = useSetters();
	const surveyDetailsSetters = useSurveyDetailsSetters();
	const surveyUserAccess = useSurveyAccessTier(survey._id).tier;

	useCompareDraftToSaved({ surveyId: survey._id, formRef });

	const { storeSurvey } = useSurvey({ surveyId: survey._id });

	const onSuccess = async () => {
		toast.success("Survey has been saved");
	};

	const onError = async (error, mutation) => {
		if (error?.data?.message?.endsWith("Survey name must be unique")) {
			setters.setInvalidNameMessage("A survey with this name already exists");
		} else if (
			error.data?.message.includes("Validation failed.") &&
			error.data?.details?.includes("String must contain at most 250 character(s)")
		) {
			setters.setInvalidNameMessage("Max character count is 250");
		} else {
			//default error handling is turned off for this mutation
			const messages = formatErrorMessage(error, mutation);

			messages.forEach((message) => {
				toast.error(message);
			});
		}
	};

	const { mutate: updateSurvey } = useUpdateSurvey(survey._id, { onSuccess, onError });

	const { isQuestionInSurvey } = useSurvey({ surveyId: survey._id });

	useEffect(() => {
		if (survey) {
			storeSurvey(survey);
		}
		return () => {
			actions.resetStore();
		};
	}, [JSON.stringify(survey)]);

	useEffect(() => {
		if (hasChanges) {
			setIsUnsafe();
		} else {
			setIsSafe();
		}
	}, [hasChanges]);

	const handleSaveSurvey = (value: SurveyModelJson) => {
		const newQuestions = value.questions;

		actions.validateSurveyUpdate((name) => {
			// additional validation for published surveys
			if (surveyStatus !== "draft") {
				if (newQuestions.filter((q) => !q.deleted && q.type !== "instructions").length === 0) {
					setters.setBuilderBanner({
						message: "Published surveys require at least one answerable question at all times.",
						color: "red",
						icon: faExclamationTriangle,
					});
					return;
				}
			}
			if (surveyStatus === "closed") {
				updateSurvey({ name });
			} else {
				const update: Omit<UpdateSurveyRequestJson, "startDate" | "endDate"> & {
					startDate?: Date;
					endDate?: Date | null;
				} = {};
				if (JSON.stringify(survey.questions) !== JSON.stringify(newQuestions)) {
					update.questions = newQuestions;
				}
				if (survey.name !== name) {
					update.name = name;
				}
				if (Object.keys(update).length > 0) {
					updateSurvey(update);
				}
			}
		});
	};

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	return (
		<BasePageTemplate title="Survey Builder" backgroundColor="grey">
			<div className="m-auto mt-[24px]">
				<PYForm
					ref={formRef}
					id="survey-builder"
					schema={surveyBuilderSchema}
					defaultValues={{ questions: orderedQuestions }}
					onSubmit={({ value, meta }) => {
						const submittedQuestions = cleanQuestions(value.questions);

						const action = meta?.action || "";

						const metaAction = (meta?.metaAction as (() => void) | undefined) || (() => {});

						switch (action) {
							case "add-question":
								metaAction();
								setters.setOrderedQuestions(value.questions);
								setters.setHasChanges(true);
								break;
							case "save-survey": {
								const orderedQuestions = setters.setOrderedQuestions(submittedQuestions);
								handleSaveSurvey({ ...value, questions: orderedQuestions });
								setters.setEditingQuestion(null);
								break;
							}
							case "duplicate-question": {
								metaAction();
								setters.setOrderedQuestions(submittedQuestions);
								setters.setHasChanges(true);
								break;
							}
							case "edit-question": {
								metaAction();
								setters.setOrderedQuestions(submittedQuestions);
								break;
							}
							case "create-question": {
								metaAction();
								setters.setOrderedQuestions(submittedQuestions);
								setters.setHasChanges(true);
								setters.setEditingQuestion(null);
								break;
							}
							case "update-question": {
								metaAction();
								setters.setOrderedQuestions(submittedQuestions);
								setters.setHasChanges(true);
								setters.setEditingQuestion(null);
								break;
							}
							case "publish-survey": {
								metaAction();
								// Handle publish action
								actions.validatePublishSurvey(
									(_questions, name) => {
										surveyDetailsSetters.setValidQuestions(submittedQuestions);
										surveyDetailsSetters.setValidName(name);
										surveyDetailsSetters.setModal("publish-survey");
										setters.setOrderedQuestions(submittedQuestions);
										setters.setHasChanges(true);
										setters.setEditingQuestion(null);
									},
									() => {
										surveyDetailsSetters.setModal("publish-survey-invalid");
									}
								);
								break;
							}
							default: {
								setters.setOrderedQuestions(submittedQuestions);
								setters.setHasChanges(true);
								setters.setEditingQuestion(null);
								break;
							}
						}
					}}
					validateOn={["onSubmit"]}
				>
					<div className="flex justify-center gap-4">
						<Card className="w-[800px]" padding="40px">
							<TitleInput defaultTitle={surveyName || ""} />
							<Spacer height={3} />
							<div ref={bannerRef} className="w-full">
								{builderBanner && (
									<>
										<PageBanner
											color={builderBanner.color}
											size="large"
											mainTitle={builderBanner.message}
											icon={builderBanner.icon}
											onDismiss={builderBanner.onDismiss}
										/>
										<Spacer height={3} />
									</>
								)}
							</div>
							<FormList<SurveyQuestionJson>
								id="questions"
								input={(field) => {
									const { value } = field;
									return (
										<>
											<div
												className={`w-full ${survey.status === "closed" ? "cursor-not-allowed" : ""}`}
											>
												<div
													style={{
														...(survey.status === "closed" && { pointerEvents: "none" }),
													}}
												>
													<DndContext
														sensors={sensors}
														collisionDetection={closestCenter}
														onDragEnd={
															survey.status === "closed"
																? () => {
																		toast.info("Survey is closed");
																	}
																: (event) => {
																		const { active, over } = event;
																		if (active.id !== over?.id) {
																			// Update form data directly
																			const fieldValue =
																				field.value as SurveyQuestionJson[];
																			const oldIndex = fieldValue.findIndex(
																				(item) => item._id === active.id
																			);
																			const newIndex = fieldValue.findIndex(
																				(item) => item._id === over?.id
																			);

																			if (oldIndex !== -1 && newIndex !== -1) {
																				setters.setOrderedQuestions(
																					field.shiftValue(oldIndex, newIndex)
																				);
																				setters.setHasChanges(true);
																			}
																		}
																	}
														}
														modifiers={[restrictToVerticalAxis, restrictToParentElement]}
													>
														<SortableContext
															items={value?.map((q) => q._id)}
															strategy={verticalListSortingStrategy}
															disabled={
																survey.status === "closed" ||
																surveyUserAccess !== "edit"
															}
														>
															<div ref={ref} className="w-full max-w-full">
																{field.value?.map(
																	(question: SurveyQuestionJson, index: number) => {
																		// Debug logging to check for empty IDs
																		if (
																			!question._id ||
																			question._id.trim() === ""
																		) {
																			console.warn(
																				"Question with empty ID found:",
																				question,
																				"at index:",
																				index
																			);
																		}
																		return question?._id ===
																			editingQuestion?._id?.toString() &&
																			survey.status !== "closed" &&
																			surveyUserAccess === "edit" ? (
																			<FormSubscribe
																				key={question._id}
																				id="questions"
																				selector={(state) => {
																					// Find question by _id instead of index to handle reordering correctly
																					const currentQuestion =
																						state.values.questions.find(
																							(q) =>
																								q._id === question._id
																						);
																					return [
																						currentQuestion || question,
																						state.values.questions,
																					];
																				}}
																			>
																				{([question]) => {
																					// Find the current index in the reordered array
																					const currentIndex =
																						field.value.findIndex(
																							(q) =>
																								q._id === question._id
																						);
																					return (
																						<EditQuestionBlock
																							field={field}
																							index={
																								currentIndex !== -1
																									? currentIndex
																									: index
																							}
																							data={question}
																							surveyStatus={survey.status}
																							publishedQuestion={
																								survey.status !==
																									"draft" &&
																								isQuestionInSurvey(
																									question._id
																								)
																							}
																						/>
																					);
																				}}
																			</FormSubscribe>
																		) : (
																			<FormSubscribe
																				key={question._id}
																				id="questions"
																				selector={(state) => {
																					// Find question by _id instead of index to handle reordering correctly
																					const currentQuestion =
																						state.values.questions.find(
																							(q) =>
																								q._id === question._id
																						);
																					return [
																						currentQuestion || question,
																						state.values.questions.length,
																						state.values.questions,
																					];
																				}}
																			>
																				{([question]) => (
																					<QuestionBlock
																						data={question}
																						field={field}
																						index={index}
																						_id={question._id}
																					/>
																				)}
																			</FormSubscribe>
																		);
																	}
																)}
															</div>
														</SortableContext>
													</DndContext>
												</div>
											</div>
											<SubmitButton
												dangerouslySetClassName="mt-6  w-full"
												iconLeft={faPlus}
												disabled={survey.status === "closed" || surveyUserAccess !== "edit"}
												onSubmitMeta={{
													action: "add-question",
													metaAction: () => {
														//add question
														const newQuestion: SurveyQuestionJson = {
															_id: new ObjectID().toString(),
															type: "text",
															text: "",
															order: field.value.length,
														};
														field.pushValue(newQuestion);

														// scroll to the new question
														const newQuestionElement = document.getElementById(
															newQuestion._id
														);
														if (newQuestionElement) {
															newQuestionElement.scrollIntoView({ behavior: "smooth" });
														}

														// set the editing question
														setters.setEditingQuestion({
															_id: new ObjectID(newQuestion._id),
															isNew: true,
														});
														setters.setHasChanges(true);
													},
												}}
											>
												New Question
											</SubmitButton>
										</>
									);
								}}
							/>
						</Card>
						<div className="w-[208px] flex-shrink-0">
							<Card className="sticky top-2 flex h-fit w-[176px] flex-col gap-2" padding="16px">
								<SubmitButton
									color="green"
									iconLeft={faSave}
									onSubmitMeta={{
										action: "save-survey",
									}}
									data-testid="save-survey-button"
									disabled={(!hasChanges && !editingQuestion) || surveyUserAccess !== "edit"}
								>
									Save Survey
								</SubmitButton>
							</Card>
						</div>
					</div>
				</PYForm>
			</div>
		</BasePageTemplate>
	);
}
